import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import guidesData from '../data/guides.json';
import '../styles/Guides.css';

const Guides = () => {
  const [currentGuideIndex, setCurrentGuideIndex] = useState(0);
  const carouselRef = useRef(null);
  const thumbnailRefs = useRef([]);
  const [carouselTransform, setCarouselTransform] = useState(0);
  const carouselTransformRef = useRef(0);

  const currentGuide = guidesData[currentGuideIndex];
  
  // Keep ref in sync with state
  useEffect(() => {
    carouselTransformRef.current = carouselTransform;
  }, [carouselTransform]);
  
  // Get the current category from the selected guide
  const selectedCategory = currentGuide?.category || guidesData[0]?.category || 'Background';

  // Get unique categories (only actual categories from guides, no A-Z)
  const categories = Array.from(new Set(guidesData.map(g => g.category)));

  // Shared function to position a guide at a specific index
  // Used by all navigation methods (arrows, tabs, thumbnails) to ensure consistent behavior
  const positionGuideAtIndex = (index) => {
    // Wait for DOM to update (margins change when active class changes)
    // Use a small timeout to ensure margins have fully updated
    setTimeout(() => {
      const thumbnail = thumbnailRefs.current[index];
      const carousel = carouselRef.current;
      
      if (!thumbnail || !carousel) {
        console.error('Missing refs after render!');
        return;
      }
      
      // Detect mobile (480px breakpoint matches CSS)
      const isMobile = window.innerWidth <= 480;
      
      // Get the actual current transform from the DOM element's computed style
      const computedStyle = window.getComputedStyle(carousel);
      const matrix = new DOMMatrix(computedStyle.transform);
      const currentTransform = matrix.m41; // m41 is the translateX value
      
      // Get positions after DOM updates (margins should be updated now)
      const thumbnailRect = thumbnail.getBoundingClientRect();
      const container = carousel.parentElement.parentElement;
      const containerRect = container.getBoundingClientRect();
      
      // Current visual position relative to container (includes current transform)
      const currentVisualPosition = thumbnailRect.left - containerRect.left;
      
      // Target position - adjust by 16px on mobile
      const isLast = index === guidesData.length - 1;
      const mobileOffset = isMobile ? -16 : 0;
      const targetPositionFromContainer = index === 0 
        ? (32 + mobileOffset) 
        : (isLast ? (64 + mobileOffset) : (48 + mobileOffset));
      
      // Calculate how much we need to adjust from current position
      const positionDelta = currentVisualPosition - targetPositionFromContainer;
      
      // Adjust transform by the delta (this prevents accumulation by always calculating from current state)
      const targetTransform = currentTransform - positionDelta;
      
      // Update both ref and state
      carouselTransformRef.current = targetTransform;
      setCarouselTransform(targetTransform);
    }, 50);
  };

  // Navigate carousel by +/- 1 using transform
  const navigateCarousel = (direction) => {
    const newIndex = currentGuideIndex + direction;
    if (newIndex >= 0 && newIndex < guidesData.length) {
      // Update state first to trigger scale animation
      setCurrentGuideIndex(newIndex);
      // Use shared positioning function
      positionGuideAtIndex(newIndex);
    }
  };

  // Handle category tab click - transform to first guide in that category
  const handleCategoryChange = (category) => {
    const firstIndexInCategory = guidesData.findIndex(g => g.category === category);
    if (firstIndexInCategory !== -1) {
      // Update state first to trigger scale animation
      setCurrentGuideIndex(firstIndexInCategory);
      // Use shared positioning function
      positionGuideAtIndex(firstIndexInCategory);
    }
  };

  // Handle thumbnail click - use transform to position clicked guide
  const handleThumbnailClick = (index) => {
    // Update state first to trigger scale animation
    setCurrentGuideIndex(index);
    // Use shared positioning function
    positionGuideAtIndex(index);
  };

  // Scroll to current guide's thumbnail when guide changes (only for arrow navigation)
  // Thumbnail clicks handle their own scrolling

  return (
    <div className="guides-page">
      {/* Main Content Area */}
      <main className="guides-main-content">
        {/* Custom Header */}
        <header className="guides-header">
          <Link to="/" className="guides-logo">
            <svg width="36" height="18" viewBox="0 0 36 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="9" cy="9" r="9" fill="#040404" />
              <path d="M18 18C19.1819 18 20.3522 17.7672 21.4442 17.3149C22.5361 16.8626 23.5282 16.1997 24.364 15.364C25.1997 14.5282 25.8626 13.5361 26.3149 12.4442C26.7672 11.3522 27 10.1819 27 9C27 7.8181 26.7672 6.64778 26.3149 5.55585C25.8626 4.46392 25.1997 3.47177 24.364 2.63604C23.5282 1.80031 22.5361 1.13738 21.4442 0.685084C20.3522 0.232791 19.1819 -4.45065e-07 18 -3.93402e-07L18 9L18 18Z" fill="#040404" />
              <path d="M27 18C28.1819 18 29.3522 17.7672 30.4442 17.3149C31.5361 16.8626 32.5282 16.1997 33.364 15.364C34.1997 14.5282 34.8626 13.5361 35.3149 12.4442C35.7672 11.3522 36 10.1819 36 9C36 7.8181 35.7672 6.64778 35.3149 5.55585C34.8626 4.46392 34.1997 3.47177 33.364 2.63604C32.5282 1.80031 31.5361 1.13738 30.4442 0.685084C29.3522 0.232791 28.1819 -4.45065e-07 27 -3.93402e-07L27 9L27 18Z" fill="#040404" />
            </svg>
          </Link>
          <div className="guides-header-divider"></div>
          <p className="guides-header-title">Guides</p>
        </header>
        <div className="guides-content-wrapper">
          <div className="guides-text-content">
            <h2 className="guides-title">{currentGuide.title}</h2>
            <p 
              className="guides-description"
              dangerouslySetInnerHTML={{ __html: currentGuide.description.replace(/\n/g, '<br />') }}
            />
            <div className="guides-category-tabs">
              {categories.map(category => (
                <button
                  key={category}
                  className={`guides-category-tab ${category === selectedCategory ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(category)}
                  aria-selected={category === selectedCategory}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
          <div className="guides-image-content">
            <img
              src={currentGuide.mainImage}
              alt={currentGuide.title}
              className="guides-main-image"
            />
          </div>
        </div>
      </main>

      {/* Fixed Carousel at Bottom */}
      <div className="guides-carousel-container">
        <div className="guides-carousel-controls">
          <button
            className="guides-carousel-arrow guides-carousel-arrow-left"
            onClick={() => navigateCarousel(-1)}
            disabled={currentGuideIndex === 0}
            aria-label="Previous guide"
          >
            <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.625 0.625L0.625 4.625L4.625 8.625" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <button
            className="guides-carousel-arrow guides-carousel-arrow-right"
            onClick={() => navigateCarousel(1)}
            disabled={currentGuideIndex === guidesData.length - 1}
            aria-label="Next guide"
          >
            <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.625 0.625L4.625 4.625L0.625 8.625" stroke="#040404" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>
        <div className="guides-carousel-wrapper">
          <div
            className="guides-carousel"
            ref={carouselRef}
            style={{ transform: `translateX(${carouselTransform}px)` }}
          >
          {guidesData.map((guide, index) => {
            const isFirst = index === 0;
            const isLast = index === guidesData.length - 1;
            const isActive = index === currentGuideIndex;
            return (
              <div
                key={guide.id}
                ref={el => thumbnailRefs.current[index] = el}
                className={`guides-thumbnail ${isActive ? 'active' : ''} ${isFirst ? 'first' : ''} ${isLast ? 'last' : ''}`}
                onClick={() => handleThumbnailClick(index)}
              >
                <img
                  src={guide.thumbnailImage}
                  alt={guide.title}
                  className="guides-thumbnail-image"
                />
                <div className="guides-thumbnail-category">{guide.category}</div>
                <div className="guides-thumbnail-title">{guide.title}</div>
              </div>
            );
          })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Guides;
